/**
 * Video Tile Component
 * Displays a single video stream (local or remote)
 */

import React, { useEffect, useRef } from 'react';
import type { VideoTileProps } from '@/api/types/webrtc.types';
import './VideoTile.css';

export const VideoTile: React.FC<VideoTileProps> = ({
  stream,
  displayName,
  isMuted = false,
  isLocal = false,
  connectionState,
  isSpeaking = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Attach stream to video element
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return;

    video.srcObject = stream;

    // Play video (required for some browsers)
    video.play().catch((err) => {
      console.error('Failed to play video:', err);
    });

    return () => {
      if (video.srcObject) {
        video.srcObject = null;
      }
    };
  }, [stream]);

  // Connection state indicator
  const getConnectionStateClass = () => {
    if (!connectionState) return '';

    switch (connectionState) {
      case 'new':
      case 'connecting':
        return 'connecting';
      case 'connected':
        return 'connected';
      case 'disconnected':
      case 'failed':
      case 'closed':
        return 'disconnected';
      default:
        return '';
    }
  };

  return (
    <div
      className={`video-tile ${isLocal ? 'local' : 'remote'} ${
        isSpeaking ? 'speaking' : ''
      } ${getConnectionStateClass()}`}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal || isMuted} // Local video is always muted to avoid echo
        className="video-element"
      />

      {/* Display name overlay */}
      <div className="video-overlay">
        <div className="display-name">
          {displayName}
          {isLocal && ' (You)'}
        </div>

        {/* Connection status indicator */}
        {connectionState && connectionState !== 'connected' && !isLocal && (
          <div className="connection-status">
            {connectionState === 'connecting' && '🔄 Connecting...'}
            {connectionState === 'disconnected' && '⚠️ Disconnected'}
            {connectionState === 'failed' && '❌ Connection failed'}
          </div>
        )}

        {/* Muted indicator */}
        {isMuted && <div className="muted-indicator">🔇</div>}

        {/* Speaking indicator */}
        {isSpeaking && <div className="speaking-indicator">🎙️</div>}
      </div>

      {/* No stream placeholder */}
      {!stream && (
        <div className="no-stream-placeholder">
          <div className="placeholder-icon">👤</div>
          <div className="placeholder-text">No video</div>
        </div>
      )}
    </div>
  );
};
